<template>
    <div v-if="showViewModeNavbar" ref="viewNavbar" :class="['ufo--nav-bar', 'dark', 'view-nav-bar']" >

        <div class="left">
            <div @click.stop="goHome" class="logo">
                <InlineSvg :src="logo" class="dark" />
            </div>

            <div>
                <NavBarPill :disabled="isDisabled || isLoading" :loading="isLoading" label="Back to editor" icon-name="back" @button-clicked="goBack" dark-scheme />
            </div>
        </div>

        <div class="mid">
            <UrlEditNavContainer variant="view" />
        </div>

        <div class="right">
            <UpgradeToUnlockNavContainer v-if="!isCaseStudy" :is-view-mode="true" :upgrade-dropdown-open="upgradeDropdown" :openNavigationDropdown="openNavigationDropdown" />
        </div>
    </div>
    <div v-if="showCaseStudyBanner && !isBannerClosed" ref="navBanner" class="nav-banner">
        <div class="title">
            <Icon v-if="isCaseStudyDraft" :width="16" :height="16" icon-name="hidden" />
            <span>{{ isCaseStudyDraft ? 'Hidden case study.' : 'Password-protected.' }}</span>
        </div>
        <div class="description">
            {{ isCaseStudyDraft
                ? 'This page is hidden from your portfolio, it is only visible for you.'
                : 'Visitors need to enter a password to view this case study.' }}
        </div>
        <div class="close-icon">
            <Cta dark-scheme :type="'icon subtle no-bg'" @click="closeBanner()">
                <Icon icon-name="close" alt="Close banner" :width="20" :height="20"/>
            </Cta>
        </div>
    </div>
</template>

<script>

import InlineSvg from "vue-inline-svg";
import uxFolioLogoSymbol from "../../../images/logo-symbol-white.svg";
import NavBarPill from "../../common/editorNavBar/NavBarPill.vue";
import UrlEditNavContainer from "./common/UrlEditNavContainer.vue";
import { mapGetters } from "vuex";
import UpgradeToUnlockNavContainer from "@/scripts/common/editorNavBar/UpgradeToUnlockNavContainer.vue";
import Cta from "@/scripts/common/components/CtaButton.vue";
import Icon from "../../common/Icon.vue";
import { clearEditorViewMode } from "@/scripts/auth/keycloakAuth.js";

export default {
    name: "view-nav-bar",
    components: {
        Cta,
        Icon,
        UpgradeToUnlockNavContainer,
        UrlEditNavContainer,
        NavBarPill,
        InlineSvg
    },
    data() {
        return {
            isSmallScreen: false,
            logo: uxFolioLogoSymbol,
            upgradeDropdown:false,
            isBannerClosed: false,
        }
    },
    async beforeMount() {
        this.setScreenSize();

        // Fix for missing store state
        if (!this.$store.state?.portfolioPages?.length && this.viewData.portfolioPages?.length) {
            this.$store.state.portfolioPages = this.viewData.portfolioPages
        }
    },
    mounted() {
        window.addEventListener("resize", this.setScreenSize, true);
        this.emitter.on('open-navigation-popover-review', () => {
            this.openNavigationDropdown('review')
        });
        this.$nextTick(() => this.applyFixedTopOffset());
    },
    unmounted() {
        window.removeEventListener("resize", this.setScreenSize, true);
        this.clearFixedTopOffset();
    },
    watch: {
        showViewModeNavbar() {
            this.$nextTick(() => this.applyFixedTopOffset());
        },
        showCaseStudyBanner() {
            this.$nextTick(() => this.applyFixedTopOffset());
        },
        isBannerClosed() {
            this.$nextTick(() => this.applyFixedTopOffset());
        },
    },
    methods: {
        goHome() {
            clearEditorViewMode();
            window.location.href = "/home"
        },
        goBack() {
            clearEditorViewMode();
            this.$store.commit("setTriggerAnimation", false);
            if(this.isHomePage) {
                this.$store.commit("setPageToEdit", "home");
                this.$store.commit("setNavigateFromPreview", true);
                this.$router.replace(`/home/portfolios/${this.viewData.portfolio.shortId}`);
            } else {
                if (this.isPortfolioPage) {
                    this.$store.commit("setPageToEdit", this.viewData.project.shortId);
                }
                this.$store.commit("setNavigateFromPreview", true);
                this.$router.replace(`/home/portfolios/${this.viewData.portfolio.shortId}/p/${this.viewData.project.shortId}`);
            }
        },
        setScreenSize() {
            this.isSmallScreen = window.innerWidth < 1024
            this.$nextTick(() => this.applyFixedTopOffset());
        },
        openNavigationDropdown(dropdown) {
            this[`${dropdown}Dropdown`] = !this[`${dropdown}Dropdown`];
        },
        closeBanner() {
            this.isBannerClosed = true;
        },
        applyFixedTopOffset() {
            const appEl = document.getElementById("app");
            if (!appEl) return;

            if (!this.showViewModeNavbar && this.$route?.meta?.type !== 'review') {
                appEl.style.removeProperty("--portfolioFixedTopOffset");
                return;
            }

            const navHeight = this.$refs?.viewNavbar?.offsetHeight || 60;
            const bannerHeight = this.showCaseStudyBanner && !this.isBannerClosed
                ? (this.$refs?.navBanner?.offsetHeight || 0)
                : 0;

            appEl.style.setProperty("--portfolioFixedTopOffset", `${navHeight + bannerHeight}px`);
        },
        clearFixedTopOffset() {
            const appEl = document.getElementById("app");
            if (!appEl) return;
            appEl.style.removeProperty("--portfolioFixedTopOffset");
        },
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        isCaseStudy() {
            return !!this.$store.state.project && this.$store.getters.isCaseStudy;
        },
        isPortfolioPage() {
            return this.$store.state.portfolioPages.some(p => p.shortId === this.$store.state.project.shortId)
        },
        isHomePage() {
            return this.$store.getters.isHomePage;
        },
        isOwnerIsLoggedIn() {
            return this.viewData.loggedInUserId === this.$store.state.project.ownerId
        },
        isLoading() {
            return !this.$store.state.view.viewModeReady && !this.$store.state.view.viewModePreloadFailed
        },
        isDisabled() {
            return this.$store.state.view.viewModePreloadFailed
        },
        isCaseStudyDraft() {
            return this.isCaseStudy && this.$store.state.project.draft
        },
        isCaseStudyPasswordProtected() {
            return this.isCaseStudy && !!this.$store.state.project.password
        },
        showViewModeNavbar() {
            const isTemplatePreview = this.$route?.query?.templatePreview === "true"
                || this.$route?.query?.templatePreview === true;

            return !!this.$store.state.project
                && this.$route?.meta?.type !== 'review'
                && this.$route?.meta?.type !== 'jfc'
                && !isTemplatePreview
                && !this.isInMobileEditorPreview
                && this.isOwnerIsLoggedIn;
        },
        // The phone editor shows this page in an iframe and has its own nav bar
        isInMobileEditorPreview() {
            try {
                return !!window.frameElement?.hasAttribute('data-mobile-editor-preview');
            } catch (error) {
                return false;
            }
        },
        showCaseStudyBanner() {
            return this.showViewModeNavbar && (this.isCaseStudyDraft || this.isCaseStudyPasswordProtected)
        }
    }
}
</script>

<style lang="scss">
#app:has(.view-nav-bar) .sticked {
    top: calc(var(--portfolioFixedTopOffset, 0px) + var(--portfolioPinnedNavOffset, 0px));
}

</style>

<style lang="scss" scoped>
.ufo--nav-bar {
    height: 60px;
    background-color: var(--asphalt10);
    padding: 12px 30px;

    @media (min-width: 1024px) {
        padding: 12px 30px;
    }

    display: flex;
    flex-direction: row;
    align-items: center;

    z-index: 103;
    position: sticky;
    position: -webkit-sticky;
    top: 0;
    width: 100%;

    &.dark {
        background-color: var(--asphalt100);
    }

    > .left,
    > .right {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        @media (min-width: 640px) {
            gap: 8px;
        }

        :deep(.publish) {
            margin: 0;
        }
    }
    > .left {
        margin-right: auto;
    }
    > .right {
        margin-left: auto;
    }

    .mid {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        gap: 12px;
    }

    &.dark {
        .logo {
            color: var(--asphalt20);
        }
    }
}

.logo {
    height: 24px;
    cursor: pointer;
    margin-right: 8px;
    color: var(--asphalt90);
    @media (min-width: 1024px) {
        margin-right: 24px;
    }
}

.ufo-cta {
    .btn-content {
        display: flex;
        align-items: center;
        gap: 4px;
    }
}

.account-name {
    width: 140px;
    text-align: center;
    @media (min-width: 1280px) {
        width: 234px;
    }
    @media (min-width: 1440px) {
        width: 326px;
    }
    height: 36px;
    padding: 0 12px;
    margin: 0;
    border-radius: 4px;
    background-color: var(--asphalt100);
    line-height: 36px;
    color: var(--asphalt10);
    font-size: 14px;
    font-family: var(--primarySans);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.nav-banner {
    width: 100%;
    height: 48px;
    padding: 0 30px;
    background: var(--asphalt90);
    border-top: 1px solid var(--asphalt70);
    position: sticky;
    top: 60px;
    z-index: 102;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    .title {
        font: var(--editor-ui-b3-semibold);
        color: var(--asphalt10);
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .description {
        font: var(--editor-ui-b3-regular);
        color: var(--asphalt30);
    }
    .close-icon {
        position: absolute;
        right: 30px;
        width: 24px;
        height: 24px;
    }
}
</style>
